import {Observable} from 'rxjs';
import {beginImageRequest, endImageRequest, HIDDEN_IMAGES_ELEMENT_ID} from './resource-image-cache';
import {PdfArtifactStatus, PdfImageStage, stagedImageId, stageSignature} from './pdf-artifact-stage';

/*
 * One correct implementation of "fetch a preview image and cache it".
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS SHARED CODE AND NOT A PATTERN TO COPY
 * ---------------------------------------------------------------------------
 * This block had been hand-copied into seven components. Two of them were
 * fixed on 2026-09-14 when the "preview never appears" bug was found; the
 * other five kept the broken version, because a copy does not get a fix.
 *
 * The bug, for anyone tempted to inline it again: the components appended a
 * src-less `<img>` to the hidden cache **synchronously, before the URL had
 * arrived**, under the real element id. Every later attempt found it, read it
 * as a cache hit, and never requested again — and `complete` is `true` for a
 * src-less image, so it was also treated as successfully loaded and its empty
 * `src` was copied onto the visible element. A preview that missed once stayed
 * blank until a full page reload.
 *
 * Three rules follow, and they are enforced here so they cannot drift:
 *
 *   1. **Nothing is cached until a URL exists.** The in-flight guard, not a
 *      placeholder element, is what stops the request storm — and it releases
 *      on the error path too, so a failure does not permanently blacklist an
 *      id.
 *   2. **A src-less element is never treated as a result** (`useImage`).
 *   3. **The cache id carries the artefact stage.** When PREPARE or
 *      STANDARDIZE completes, the id changes, so the earlier render can never
 *      be mistaken for the better one — and the client does not have to hope
 *      the server hands back a different signed URL
 *      (`docs/contracts/shared/PdfArtifactImages.md` Q3).
 *
 * Not framework-free — it touches the DOM and rxjs — so the *rules* worth
 * testing live in `pdf-artifact-stage.ts` and `resource-image-cache.ts`
 * instead, both of which are covered by layer-0 smoke tests.
 */

export interface StagedImageOptions {
  /** Cache id without the stage suffix, e.g. `resource-12-1-288`. */
  baseId: string;
  /** Which artefact this request is for. Becomes the id suffix. */
  stage: PdfImageStage;
  /** Issues the actual HTTP call. Called at most once per id. */
  fetchUrl: () => Observable<string>;
  /** The image is cached and loaded. Copy its `src` onto the visible element. */
  onReady: (image: HTMLImageElement) => void;
  /**
   * The server had no image. **Normal**, not an error — before preparation
   * there may be nothing to render. Nothing is cached, so a later stage change
   * produces a genuine retry.
   */
  onUnavailable?: () => void;
  onError?: (err: any) => void;
}

/**
 * Attach handlers, or use the image straight away.
 *
 * The `!image.src` guard is rule 2 above and is the difference between a
 * working preview and a permanently blank one.
 */
function useImage(image: HTMLImageElement, elementId: string, onReady: (i: HTMLImageElement) => void): void {
  if (!image || !image.src) {
    return;
  }
  if (image.complete) {
    onReady(image);
    return;
  }
  image.addEventListener('load', () => onReady(image));
  image.addEventListener('error', () => console.log(`Error loading image ${elementId}`));
}

/**
 * Fetch (or reuse) the cached page image for one artefact stage.
 *
 * Safe to call on every change-detection pass: a cache hit is a DOM lookup,
 * and a miss while a request is outstanding is refused by the in-flight guard.
 */
export function requestStagedImage(options: StagedImageOptions): void {
  if (typeof document === 'undefined' || !options || !options.baseId) {
    return;
  }
  const elementId = stagedImageId(options.baseId, options.stage);

  const cached = document.getElementById(elementId) as HTMLImageElement;
  if (cached) {
    useImage(cached, elementId, options.onReady);
    return;
  }

  const container = document.getElementById(HIDDEN_IMAGES_ELEMENT_ID);
  if (!container) {
    return;
  }

  // ⚠ Do NOT append a placeholder under `elementId` here. See the header.
  if (!beginImageRequest(elementId)) {
    return;
  }

  options.fetchUrl().subscribe(
    (url: string) => {
      endImageRequest(elementId);
      if (!url) {
        if (options.onUnavailable) { options.onUnavailable(); }
        return;
      }
      // Re-check: another component may have cached this id while we waited.
      // The projects grid renders many thumbnails off one cache.
      let element = document.getElementById(elementId) as HTMLImageElement;
      if (!element) {
        element = document.createElement('img');
        element.id = elementId;
        element.src = url;
        container.appendChild(element);
      }
      useImage(element, elementId, options.onReady);
    },
    err => {
      endImageRequest(elementId);
      if (options.onError) { options.onError(err); }
      console.log(`Error requesting image ${elementId}`, err);
    }
  );
}

/**
 * "Has anything actually changed since the last time I drew this?"
 *
 * ---------------------------------------------------------------------------
 * WHY A COMPONENT NEEDS THIS
 * ---------------------------------------------------------------------------
 * `updateNotifier` fires for **every** change to **every** row of that model
 * type — including rows belonging to other users, since notifications are
 * broadcast per role and not filtered per user
 * (`docs/contracts/shared/Notifications.md` §5). A component that re-requested
 * on every event would re-fetch every visible thumbnail whenever anybody
 * anywhere edited a title.
 *
 * So: subscribe broadly, act narrowly. Components still filter on the id they
 * are displaying; this catches the rest — a `COVER` update that changed the
 * barcode position and not `standardized` is a real update to the right row
 * that still means nothing for the image.
 */
export class StageWatch {
  private last: string = null;

  /**
   * True exactly once per meaningful change.
   *
   * "Meaningful" is `stageSignature`: the resource, the best available stage,
   * and the outstanding-work notice. `WAITING` → `IN_PROGRESS` is deliberately
   * NOT meaningful — the queue moved, the image did not.
   */
  changed(status: PdfArtifactStatus | null | undefined): boolean {
    const signature = stageSignature(status);
    if (signature === this.last) {
      return false;
    }
    this.last = signature;
    return true;
  }

  /** Forget the last signature, so the next `changed()` reports true. */
  reset(): void {
    this.last = null;
  }
}

